import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import CatEmpleados from '../../../Services/Catalogos/CatEmpleados'
import DTOEmpleado from './DTO/DTOEmpleado'
import {
  DTOEmpleadoInfoGeneral,
  DTOEmpleadoInfoGeneralDetalle,
} from './DTO/DTOEmpleadoInfoGeneral'

type ExtensionImagen = 'png' | 'jpeg' | 'gif'

interface EmpleadoExportacion {
  empleado: DTOEmpleado
  detalle: DTOEmpleadoInfoGeneralDetalle | null
  foto: { base64: string; extension: ExtensionImagen } | null
}

export interface ResultadoExcelEmpleados {
  empleados: number
  registrosHistoricos: number
  empleadosSinFoto: number
  empleadosSinInfoGeneral: number
}

const COLOR_PRINCIPAL = 'FF1F4E78'
const COLOR_SECUNDARIO = 'FFD9EAF7'
const COLOR_BORDE = 'FFB8C2CC'
const COLOR_ALTERNO = 'FFF5F8FA'

const limpiarTexto = (valor?: string | null) => valor?.trim() || ''

const fechaExcel = (valor?: string | null): Date | null => {
  if (!valor) return null

  const fechaIso = valor.slice(0, 10)
  const partesIso = fechaIso.split('-').map(Number)
  if (partesIso.length === 3 && partesIso.every(Number.isFinite)) {
    return new Date(partesIso[0], partesIso[1] - 1, partesIso[2])
  }

  const fecha = new Date(valor)
  return Number.isNaN(fecha.getTime()) ? null : fecha
}

const fechaTexto = (valor?: string | null) => {
  const fecha = fechaExcel(valor)
  if (!fecha) return ''

  return [
    String(fecha.getDate()).padStart(2, '0'),
    String(fecha.getMonth() + 1).padStart(2, '0'),
    fecha.getFullYear(),
  ].join('/')
}

const calcularAniosServicio = (fechaIngreso?: string | null): number | null => {
  const ingreso = fechaExcel(fechaIngreso)
  if (!ingreso) return null

  const hoy = new Date()
  let anios = hoy.getFullYear() - ingreso.getFullYear()
  const aunNoCumple =
    hoy.getMonth() < ingreso.getMonth()
    || (hoy.getMonth() === ingreso.getMonth() && hoy.getDate() < ingreso.getDate())

  if (aunNoCumple) anios -= 1
  return Math.max(anios, 0)
}

const vigenciaTexto = (registro?: DTOEmpleadoInfoGeneral | null) => {
  if (!registro) return ''
  return `${fechaTexto(registro.fechaVigenciaDesde)} - ${
    registro.fechaVigenciaHasta ? fechaTexto(registro.fechaVigenciaHasta) : 'Abierta'
  }`
}

const estadoRegistro = (registro?: DTOEmpleadoInfoGeneral | null) => {
  if (!registro) return 'Sin información'
  if (registro.esAnulado) return 'Anulado'
  return registro.fechaVigenciaHasta ? 'Histórico' : 'Vigente'
}

const obtenerTabulador = (
  empleado: DTOEmpleado,
  detalle: DTOEmpleadoInfoGeneralDetalle | null
) => {
  const detalleExtendido = detalle as (DTOEmpleadoInfoGeneralDetalle & { tabulador?: string }) | null
  const empleadoExtendido = empleado as DTOEmpleado & { tabulador?: string }
  return limpiarTexto(detalleExtendido?.tabulador || empleadoExtendido.tabulador) || 'Sin capturar'
}

const blobAImagen = (blob: Blob): Promise<{ base64: string; extension: ExtensionImagen } | null> => {
  const tipo = blob.type.toLowerCase()
  const extension: ExtensionImagen | null = tipo.includes('jpeg') || tipo.includes('jpg')
    ? 'jpeg'
    : tipo.includes('png')
      ? 'png'
      : tipo.includes('gif')
        ? 'gif'
        : null

  if (!extension) return Promise.resolve(null)

  return new Promise((resolve, reject) => {
    const lector = new FileReader()
    lector.onload = () => resolve({ base64: String(lector.result), extension })
    lector.onerror = () => reject(lector.error)
    lector.readAsDataURL(blob)
  })
}

const mapearConLimite = async <T, R>(
  elementos: T[],
  limite: number,
  procesar: (elemento: T) => Promise<R>
): Promise<R[]> => {
  const resultados = new Array<R>(elementos.length)
  let siguiente = 0

  const trabajador = async () => {
    while (siguiente < elementos.length) {
      const indice = siguiente++
      resultados[indice] = await procesar(elementos[indice])
    }
  }

  await Promise.all(
    Array.from({ length: Math.min(limite, elementos.length) }, () => trabajador())
  )
  return resultados
}

const obtenerDatosEmpleado = async (empleado: DTOEmpleado): Promise<EmpleadoExportacion> => {
  const numero = String(empleado.numEmpleado).trim()
  const [infoGeneral, foto] = await Promise.allSettled([
    CatEmpleados.GetInfoGeneral(numero),
    CatEmpleados.GetFotoFirmaBlob(numero, 'foto'),
  ])

  let imagen: EmpleadoExportacion['foto'] = null
  if (foto.status === 'fulfilled') {
    try {
      imagen = await blobAImagen(foto.value.data)
    } catch {
      imagen = null
    }
  }

  return {
    empleado,
    detalle: infoGeneral.status === 'fulfilled' ? infoGeneral.value.data : null,
    foto: imagen,
  }
}

const prepararHoja = (
  hoja: ExcelJS.Worksheet,
  titulo: string,
  subtitulo: string,
  encabezados: string[],
  anchos: number[]
) => {
  hoja.mergeCells(1, 1, 1, encabezados.length)
  const celdaTitulo = hoja.getCell(1, 1)
  celdaTitulo.value = titulo
  celdaTitulo.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 16 }
  celdaTitulo.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLOR_PRINCIPAL } }
  celdaTitulo.alignment = { vertical: 'middle', horizontal: 'left' }
  hoja.getRow(1).height = 28

  hoja.mergeCells(2, 1, 2, encabezados.length)
  const celdaSubtitulo = hoja.getCell(2, 1)
  celdaSubtitulo.value = subtitulo
  celdaSubtitulo.font = { italic: true, color: { argb: 'FF46535F' }, size: 10 }
  celdaSubtitulo.alignment = { vertical: 'middle', horizontal: 'left' }
  hoja.getRow(2).height = 22

  const filaEncabezado = hoja.getRow(4)
  filaEncabezado.values = encabezados
  filaEncabezado.height = 32
  filaEncabezado.eachCell((celda) => {
    celda.font = { bold: true, color: { argb: 'FFFFFFFF' } }
    celda.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLOR_PRINCIPAL } }
    celda.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true }
    celda.border = {
      top: { style: 'thin', color: { argb: COLOR_BORDE } },
      left: { style: 'thin', color: { argb: COLOR_BORDE } },
      bottom: { style: 'thin', color: { argb: COLOR_BORDE } },
      right: { style: 'thin', color: { argb: COLOR_BORDE } },
    }
  })

  anchos.forEach((ancho, indice) => {
    hoja.getColumn(indice + 1).width = ancho
  })

  hoja.views = [{ state: 'frozen', ySplit: 4, xSplit: 2 }]
  hoja.autoFilter = {
    from: { row: 4, column: 1 },
    to: { row: 4, column: encabezados.length },
  }
  hoja.pageSetup = {
    orientation: 'landscape',
    fitToPage: true,
    fitToWidth: 1,
    fitToHeight: 0,
    margins: { left: 0.25, right: 0.25, top: 0.5, bottom: 0.5, header: 0.2, footer: 0.2 },
  }
}

const estilizarFila = (
  fila: ExcelJS.Row,
  columnasMoneda: number[],
  columnasFecha: number[]
) => {
  fila.eachCell({ includeEmpty: true }, (celda, columna) => {
    celda.alignment = {
      vertical: 'middle',
      horizontal: columnasMoneda.includes(columna) ? 'right' : 'left',
      wrapText: true,
    }
    celda.border = {
      top: { style: 'thin', color: { argb: COLOR_BORDE } },
      left: { style: 'thin', color: { argb: COLOR_BORDE } },
      bottom: { style: 'thin', color: { argb: COLOR_BORDE } },
      right: { style: 'thin', color: { argb: COLOR_BORDE } },
    }
    if (fila.number % 2 === 0) {
      celda.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLOR_ALTERNO } }
    }
    if (columnasMoneda.includes(columna)) celda.numFmt = '$#,##0.00;[Red]-$#,##0.00'
    if (columnasFecha.includes(columna) && celda.value) celda.numFmt = 'dd/mm/yyyy'
  })
}

const obtenerVigente = (detalle: DTOEmpleadoInfoGeneralDetalle | null) => {
  if (!detalle) return null
  return detalle.historial.find((registro) => !registro.esAnulado && !registro.fechaVigenciaHasta)
    || detalle.vigente
    || null
}

export const generarExcelEmpleados = async (
  empleados: DTOEmpleado[]
): Promise<ResultadoExcelEmpleados> => {
  if (empleados.length === 0) {
    throw new Error('No hay empleados en el resultado actual para exportar.')
  }

  // El límite evita saturar el backend al exportar el catálogo completo.
  const datos = await mapearConLimite(empleados, 6, obtenerDatosEmpleado)
  const libro = new ExcelJS.Workbook()
  libro.creator = 'RHMEX'
  libro.created = new Date()

  const generado = new Intl.DateTimeFormat('es-MX', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date())

  const encabezadosGeneral = [
    'Foto', 'No. empleado', 'Nombre', 'Oficina', 'Puesto', 'Área',
    'Fecha de ingreso', 'Tabulador', 'Años de servicio', 'Salario diario',
    'SDI', 'Semanal bruto', 'Mensual bruto', 'Semanal neto', 'Mensual neto',
    'Automóvil', 'Gasolina mensual', 'Estado', 'Vigencia',
    'Fecha de evaluación', 'Promoción', 'Puesto resultado',
  ]
  const hojaGeneral = libro.addWorksheet('Empleados', { properties: { tabColor: { argb: COLOR_PRINCIPAL } } })
  prepararHoja(
    hojaGeneral,
    'Reporte general de empleados',
    `${empleados.length} empleado(s) según los filtros del catálogo · Generado ${generado}`,
    encabezadosGeneral,
    [10, 15, 34, 18, 28, 24, 16, 18, 13, 16, 14, 17, 17, 17, 17, 12, 17, 15, 25, 18, 12, 28]
  )

  datos.forEach(({ empleado, detalle, foto }) => {
    const vigente = obtenerVigente(detalle)
    const fila = hojaGeneral.addRow([
      '',
      String(empleado.numEmpleado).trim(),
      limpiarTexto(detalle?.name) || limpiarTexto(empleado.name),
      limpiarTexto(detalle?.oficina) || limpiarTexto(empleado.oficina),
      limpiarTexto(detalle?.puesto) || limpiarTexto(empleado.puesto),
      limpiarTexto(detalle?.departamento) || limpiarTexto(empleado.departamento),
      fechaExcel(detalle?.fechaIngreso || empleado.fechaIngreso),
      obtenerTabulador(empleado, detalle),
      calcularAniosServicio(detalle?.fechaIngreso || empleado.fechaIngreso),
      vigente?.salarioDiario ?? null,
      vigente?.sdi ?? null,
      vigente?.semanalBruto ?? null,
      vigente?.mensualBruto ?? null,
      vigente?.semanalNeto ?? null,
      vigente?.mensualNeto ?? null,
      vigente ? (vigente.tieneAutomovil ? 'Sí' : 'No') : '',
      vigente?.gasolinaMensual ?? null,
      estadoRegistro(vigente),
      vigenciaTexto(vigente),
      fechaExcel(vigente?.fechaEvaluacion),
      vigente ? (vigente.promocion ? 'Sí' : 'No') : '',
      limpiarTexto(vigente?.puestoResultado),
    ])
    fila.height = 48
    estilizarFila(fila, [10, 11, 12, 13, 14, 15, 17], [7, 20])
    fila.getCell(2).numFmt = '@'

    if (foto) {
      const imagenId = libro.addImage(foto)
      hojaGeneral.addImage(imagenId, {
        tl: { col: 0.15, row: fila.number - 0.9 },
        ext: { width: 46, height: 46 },
      })
    }
  })

  const encabezadosHistorico = encabezadosGeneral.slice(1)
  const hojaHistorico = libro.addWorksheet('Histórico', { properties: { tabColor: { argb: COLOR_SECUNDARIO } } })
  prepararHoja(
    hojaHistorico,
    'Histórico de información general',
    `Registros anteriores de los empleados incluidos en el filtro · Generado ${generado}`,
    encabezadosHistorico,
    [15, 34, 18, 28, 24, 16, 18, 13, 16, 14, 17, 17, 17, 17, 12, 17, 15, 25, 18, 12, 28]
  )

  let registrosHistoricos = 0
  datos.forEach(({ empleado, detalle }) => {
    const vigente = obtenerVigente(detalle)
    const historicos = [...(detalle?.historial || [])]
      .filter((registro) => registro.idInfoGeneral !== vigente?.idInfoGeneral)
      .sort((a, b) => {
        const fecha = a.fechaVigenciaDesde.localeCompare(b.fechaVigenciaDesde)
        return fecha !== 0 ? fecha : a.idInfoGeneral - b.idInfoGeneral
      })

    historicos.forEach((registro) => {
      registrosHistoricos += 1
      const fila = hojaHistorico.addRow([
        String(empleado.numEmpleado).trim(),
        limpiarTexto(detalle?.name) || limpiarTexto(empleado.name),
        limpiarTexto(detalle?.oficina) || limpiarTexto(empleado.oficina),
        limpiarTexto(detalle?.puesto) || limpiarTexto(empleado.puesto),
        limpiarTexto(detalle?.departamento) || limpiarTexto(empleado.departamento),
        fechaExcel(detalle?.fechaIngreso || empleado.fechaIngreso),
        obtenerTabulador(empleado, detalle),
        calcularAniosServicio(detalle?.fechaIngreso || empleado.fechaIngreso),
        registro.salarioDiario,
        registro.sdi,
        registro.semanalBruto,
        registro.mensualBruto,
        registro.semanalNeto,
        registro.mensualNeto,
        registro.tieneAutomovil ? 'Sí' : 'No',
        registro.gasolinaMensual,
        estadoRegistro(registro),
        vigenciaTexto(registro),
        fechaExcel(registro.fechaEvaluacion),
        registro.promocion ? 'Sí' : 'No',
        limpiarTexto(registro.puestoResultado),
      ])
      fila.height = 34
      estilizarFila(fila, [9, 10, 11, 12, 13, 14, 16], [6, 19])
      fila.getCell(1).numFmt = '@'
    })
  })

  if (registrosHistoricos === 0) {
    hojaHistorico.mergeCells(5, 1, 5, encabezadosHistorico.length)
    const celda = hojaHistorico.getCell(5, 1)
    celda.value = 'Los empleados seleccionados no tienen registros históricos anteriores.'
    celda.font = { italic: true, color: { argb: 'FF5B6570' } }
    celda.alignment = { vertical: 'middle', horizontal: 'center' }
    hojaHistorico.getRow(5).height = 28
  }

  hojaGeneral.getColumn(2).numFmt = '@'
  hojaHistorico.getColumn(1).numFmt = '@'

  const contenido = await libro.xlsx.writeBuffer()
  const fechaArchivo = new Date().toISOString().slice(0, 10)
  saveAs(
    new Blob([contenido], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    `Reporte_Empleados_${fechaArchivo}.xlsx`
  )

  return {
    empleados: empleados.length,
    registrosHistoricos,
    empleadosSinFoto: datos.filter((item) => !item.foto).length,
    empleadosSinInfoGeneral: datos.filter((item) => !obtenerVigente(item.detalle)).length,
  }
}
